/** @jsxRuntime classic */
/** @jsx jsx */
import { gql, useQuery } from '@apollo/client';
import { Title } from '@bottlebooks/gatsby-design-system';
import { Col, Container } from '@bottlebooks/gatsby-theme-base';
import BrandAddressSection, {
  BrandAddressSectionFragment,
} from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandAddressSection';
import BrandBanner, {
  BrandBannerFragment,
} from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandBanner';
import BrandShopsSection, {
  BrandShopsSectionFragment,
} from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandShopsSection';
import ContentRow from '@bottlebooks/gatsby-theme-event/src/components/ContentRow';
import ExhibitorActions from '@bottlebooks/gatsby-theme-event/src/components/Exhibitor/ExhibitorActions';
import ExhibitorHeaderRow, {
  ExhibitorHeaderRowFragment,
} from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorPage/ExhibitorHeaderRow';
import ExhibitorSections from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorPage/ExhibitorSections';
import PreviewLayout from '@bottlebooks/gatsby-theme-event/src/components/Layout/PreviewLayout';
import ProductCard from '@bottlebooks/gatsby-theme-event/src/components/ProductCard/ProductCard';
import ProductListListRow from '@bottlebooks/gatsby-theme-event/src/components/ProductListsList/ProductListListRow';
import ProductListsList from '@bottlebooks/gatsby-theme-event/src/components/ProductListsList/ProductListsList';
import ProductsList from '@bottlebooks/gatsby-theme-event/src/components/ProductsList/ProductsList';
import useLink from '@bottlebooks/gatsby-theme-event/src/useLink';
import { Trans } from '@lingui/macro';
import deepMerge from 'lodash/merge';
import { jsx } from 'theme-ui';
import getWfsPrice from '../../../gatsby-theme-event-pages/templates/getWfsPrice';

export default function ExhibitorPreviewPage({
  companyId,
  collectionId,
  locale,
}) {
  const productLists = [];
  const link = useLink();
  const { data, loading, error /* , refetch */ } = useQuery(clientQuery, {
    variables: { collectionId, locale, companyId },
  });
  if (loading) return <PreviewLayout locale={locale}>Loading…</PreviewLayout>;
  if (error) {
    console.error(error);
    return (
      <PreviewLayout locale={locale}>
        We're sorry, but there was an error.
      </PreviewLayout>
    );
  }

  const { exhibitor, products } = afterQuery(data);
  const hasProducts = products && products.length !== 0;
  const hasProductLists = productLists?.length > 0;
  const hasProductsSection = hasProducts || hasProductLists;
  return (
    <PreviewLayout locale={locale} sx={{ backgroundColor: 'transparent' }}>
      <BrandBanner {...exhibitor} />
      <ExhibitorHeaderRow exhibitor={exhibitor} />

      <Container>
        <ContentRow sx={{ paddingY: 3 }}>
          <Col>
            <ExhibitorActions exhibitor={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      <ExhibitorSections exhibitor={exhibitor} eventId={collectionId} />

      <BrandAddressSection brand={exhibitor} />

      <Container>
        <ContentRow>
          <Col>
            <BrandShopsSection brand={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      {hasProductsSection ? (
        <Container fluid sx={{ maxWidth: 'container.fluid' }}>
          <Title sx={{ paddingY: 4 }}>
            <Trans>Products</Trans>
          </Title>
          {hasProductLists && (
            <ProductListsList sx={{ marginX: [-3, -2], marginY: -2 }}>
              {productLists.map((productList) => (
                <ProductListListRow
                  key={productList.id}
                  productList={productList}
                  to={link.productList(productList)}
                  sx={{ marginX: [0, 2], marginY: [0, 2] }}
                />
              ))}
            </ProductListsList>
          )}
          {hasProducts && (
            <ProductsList
              count={products.length}
              sx={{ marginX: [-3, -2], marginY: -2 }}
            >
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  to={link.product(product)}
                  sx={{ marginBottom: [null, null, 3] }}
                />
              ))}
            </ProductsList>
          )}
        </Container>
      ) : null}
    </PreviewLayout>
  );
}

function afterQuery({ event }) {
  const exhibitor = {
    ...event?.exhibitor,
    ...event?.registrations?.nodes?.[0],
    shortDescription:
      event?.registrations?.nodes?.[0]?.customShortDescription?.value ||
      event?.exhibitor?.shortDescription,
  };
  const exhibitorType = exhibitor?.exhibitorType.key;
  const baseProducts = exhibitor?.products?.nodes || {};
  const products = baseProducts?.map((product) => {
    const registeredProduct = event?.registeredProducts?.nodes?.find(
      (registeredProduct) => registeredProduct?.productId === product?.productId
    );
    const price = registeredProduct?.wfsPrice.text;
    return deepMerge(
      {},
      product,
      registeredProduct,
      registeredProduct?.product,
      getWfsPrice({ exhibitorType, price })
    );
  });
  return {
    exhibitor,
    products,
  };
}

const clientQuery = gql`
  query ExhibitorPreviewPage(
    $collectionId: ID!
    $locale: ContentLocale
    $companyId: ID!
  ) {
    event(eventId: $collectionId, locale: $locale) {
      eventId # used for the cache.
      registrations(filter: { companyId: { eq: $companyId } }) {
        nodes {
          ...ExhibitorActions_SingleRegistration
          customShortDescription: customFieldValue(
            key: "__inject__shortDescription"
          ) {
            ... on TextFieldValue {
              value
            }
          }
          exhibitorType: customFieldValue(key: "exhibitorType") {
            __typename
            ... on SelectFieldValue {
              key
            }
          }
        }
      }
      registeredProducts {
        nodes {
          productId
          ...ProductCard_RegisteredProduct
          wfsPrice: customFieldValue(key: "wfsPrice") {
            ... on TextFieldValue {
              text
            }
          }
        }
      }
      exhibitor(exhibitorId: $companyId) {
        exhibitorId # used for calculating the cache entry.
        ...BrandBanner
        ...BrandAddressSection
        ...ExhibitorHeaderRow
        ...ExhibitorActions
        ...ExhibitorSections
        ...BrandShopsSection
        products {
          nodes {
            productId # used for the cache.
            id: productId
            slug: productId
            ...useLink_Product
            producer {
              producerId # used for the cache.
            }
            ... on ExtendedProduct {
              exhibitor {
                exhibitorId # needed for the apolloClient cache.
              }
            }
            ...ProductCard
          }
        }
      }
    }
  }
  ${BrandBannerFragment}
  ${BrandAddressSectionFragment}
  ${ExhibitorHeaderRowFragment}
  ${ExhibitorActions.fragment}
  ${ExhibitorSections.fragment}
  ${BrandShopsSectionFragment}
  ${ProductCard.fragment}
  ${useLink.productFragment}
`;
